import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { BillingReportingDateRange, BillingReportingTable } from '../../../components/reporting';
import { LineChart } from '../../../components/charts';
import LoadScreen from '../../../components/load-screen';

import { getSurgeons } from '../../../actions/surgeonsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getProcedures } from '../../../actions/proceduresActions';
import { getCasesWithUsage } from '../../../actions/casesActions';

import {
  prepareCasesProformaValueMonthly, prepareCasesProformaValueQuarterly, prepareCasesProformaValueDaily
} from '../../../utils/analytics';

import { presets } from '../../../constants/analytics';
import routes from '../../../constants/routes';
import { onPresetChange as setPresetDates } from '../../../utils/date';

import './reporting-page.scss';

const presetOptions = [
  { label: 'This month', value: presets.THIS_MONTH },
  { label: 'This quarter', value: presets.THIS_QUARTER },
  { label: 'This year', value: presets.THIS_YEAR },
  { label: 'Last month', value: presets.LAST_MONTH },
  { label: 'Last quarter', value: presets.LAST_QUARTER },
  { label: 'Last year', value: presets.LAST_YEAR },
];

const filteredFields = ['surgeonName', 'hospitalName', 'procedureName'];
const getInitialFilters = () => {
  const initialFilter = {};

  filteredFields?.forEach((field) => {
    initialFilter[field] = [];
  });

  return initialFilter;
};

const formatAxis = (v) => {
  const value = Number(v) || 0;
  const formatter = Intl.NumberFormat('en', { notation: 'compact' });

  return formatter.format(value);
};

const formatTooltip = (value) => {
  if (!value || !Number(value)) {
    return '$0.00';
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(value);
}

const hasProformaSentMinimum = (caseItem) => {
  return !!caseItem?.proformaSent || !!caseItem?.proformaSentAt;
};

const getPresetRange = (value, yearAgo = false) => {
  let minDate;
  let maxDate;

  setPresetDates(value, {
    onMinDateChange: (date) => { minDate = date; },
    onMaxDateChange: (date) => { maxDate = date; },
  }, yearAgo);

  return { minDate, maxDate };
};

const BillingReportingPage = ({ proformaSentMinimum = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const tenantPrimaryColor = useSelector((state) => state.tenant.currentTenant.colorPrimary);
  const tenantId = useSelector((state) => state.tenant.currentTenant.id);
  const surgeons = useSelector((state) => state.surgeons.list);
  const hospitals = useSelector((state) => state.hospitals.list);
  const procedures = useSelector((state) => state.procedures.list);
  const users = useSelector((state) => state.users.list);

  const [loading, setLoading] = useState(false);

  const [preset, setPreset] = useState(presets.THIS_MONTH);
  const [dateRange, setDateRange] = useState(() => getPresetRange(presets.THIS_MONTH));
  const { minDate, maxDate } = dateRange;

  const [presetCompare, setPresetCompare] = useState('');
  const [isChecked, setChecked] = useState(false);

  const [filter, setFilter] = useState(getInitialFilters());
  const [cases, setCases] = useState([]);

  const compareRange = useMemo(() => {
    if (!presetCompare) {
      return null;
    }

    const comparePreset = {
      [presets.THIS_MONTH]: presets.LAST_MONTH,
      [presets.THIS_QUARTER]: presets.LAST_QUARTER,
      [presets.THIS_YEAR]: presets.LAST_YEAR,
    }[preset];

    return comparePreset ? getPresetRange(comparePreset, presetCompare === presets.YEAR_AGO) : null;
  }, [preset, presetCompare]);
  const minDateCompare = compareRange?.minDate;
  const maxDateCompare = compareRange?.maxDate;
  const minDateValue = minDate?.valueOf();
  const maxDateValue = maxDate?.valueOf();
  const minDateCompareValue = minDateCompare?.valueOf();
  const maxDateCompareValue = maxDateCompare?.valueOf();

  useEffect(() => {
    if (!tenantId) {
      return;
    }

    Promise.all([
      dispatch(getSurgeons()),
      dispatch(getHospitals()),
      dispatch(getProcedures()),
    ]).catch((err) => console.error(err));
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (!tenantId || !minDate || !maxDate || minDateValue >= maxDateValue) {
      setCases([]);
      setLoading(false);
      return;
    }

    let active = true;
    const dateRanges = [dateRange];
    if (compareRange) {
      dateRanges.push(compareRange);
    }

    setCases([]);
    setLoading(true);
    dispatch(getCasesWithUsage({
      dateRanges,
      dateField: isChecked ? 'orderClosedAt' : 'date',
      billingStatus: proformaSentMinimum ? 'proformaSent' : 'closed',
    }))
      .then((resp) => {
        if (active) {
          setCases(resp.filter((item) => !!item.usage.length));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => { active = false; };
  }, [dispatch, tenantId, minDateValue, maxDateValue, minDateCompareValue, maxDateCompareValue, isChecked, presetCompare, proformaSentMinimum]);

  const handlePresetChange = (value) => {
    setPreset(value);
    setPresetCompare('');
    setDateRange(getPresetRange(value));
  };

  const handleDateChange = (field, date) => {
    setDateRange((current) => ({
      ...current,
      [field]: date ? moment(date)[field === 'minDate' ? 'startOf' : 'endOf']('day') : null,
    }));
    setPreset(null);
    setPresetCompare('');
  };

  const getChartData = (filteredCases, min, max) => {
    const requireOrderClosed = !proformaSentMinimum;

    if (min.month() === max.month() && min.year() === max.year()) {
      return prepareCasesProformaValueDaily(filteredCases, min.daysInMonth(), requireOrderClosed);
    }

    if (min.quarter() === max.quarter() && min.year() === max.year()) {
      return prepareCasesProformaValueQuarterly(filteredCases, min.quarter(), requireOrderClosed);
    }

    return prepareCasesProformaValueMonthly(filteredCases, requireOrderClosed);
  };

  const hasBillingStatus = (caseItem) => {
    return proformaSentMinimum ? hasProformaSentMinimum(caseItem) : !!caseItem.orderClosed;
  };

  const handleFilterChange = (field, values) => {
    setFilter({ ...filter, [field]: values });
  };

  const getFilterOptions = () => {
    const hospitalsOptions = [];
    const proceduresOptions = [];
    const surgeonsOptions = [];

    cases?.forEach((item) => {
      const { hospital, procedure, surgeon } = item;

      if (hospital && !hospitalsOptions?.map((item) => item.id)?.includes(hospital)) {
        const hospitalObj = hospitals?.find((item) => item.id === hospital);
        if (hospitalObj) {
          hospitalsOptions?.push(hospitalObj);
        }
      }

      if (procedure && !proceduresOptions?.map((item) => item.id)?.includes(procedure)) {
        const procedureObj = procedures?.find((item) => item.id === procedure);
        if (procedureObj) {
          proceduresOptions?.push(procedureObj);
        }
      }

      if (surgeon && !surgeonsOptions?.map((item) => item.id)?.includes(surgeon)) {
        const surgeonObj = surgeons?.find((item) => item.id === surgeon);
        if (surgeonObj) {
          surgeonsOptions?.push(surgeonObj);
        }
      }
    });

    return { hospitalName: hospitalsOptions, procedureName: proceduresOptions, surgeonName: surgeonsOptions };
  };

  const handleRemoveFilterItem = (key, value) => {
    setFilter({ ...filter, [key]: filter[key].filter((item) => item !== value) });
  };

  // Memoized data

  const filteredCases = useMemo(() => (!minDate || !maxDate ? [] : cases.filter((item) => {
    let persistence = true;

    Object.keys(filter)
      .forEach((key) => {
        const field = key.split('Name')[0];
        if (filter[key].length && !filter[key].includes(item[field])) {
          persistence = false;
        }
      });

    return persistence && minDate.isBefore(item[isChecked ? 'orderClosedAt' : 'date']) && maxDate.isAfter(item[isChecked ? 'orderClosedAt' : 'date']) && hasBillingStatus(item);
  })), [cases, minDate, maxDate, filter, isChecked, proformaSentMinimum]);

  const casesChartData = useMemo(() => {
    if (!minDate || !maxDate) {
      return [];
    }

    const data = getChartData(filteredCases.map((caseItem) => ({ ...caseItem, date: isChecked ? caseItem?.orderClosedAt : caseItem?.date })), minDate, maxDate);

    if (compareRange) {
      const comparedCases = cases
        .filter((item) => minDateCompare.isBefore(item[isChecked ? 'orderClosedAt' : 'date']) && maxDateCompare.isAfter(item[isChecked ? 'orderClosedAt' : 'date']) && hasBillingStatus(item))
        .map((caseItem) => ({ ...caseItem, date: isChecked ? caseItem?.orderClosedAt : caseItem?.date }));
      const compareData = getChartData(comparedCases, minDateCompare, maxDateCompare);

      if (preset === presets.THIS_QUARTER && presetCompare === presets.PREVIOUS_PERIOD) {
        const previousQuarter = compareData?.map((item) => ({
          data: item.data?.map((i, index) => ({ x: data[0]?.data[index]?.x, y: i.y })),
          id: `${item.id}_compare`
        }));
        return [...previousQuarter, ...data];
      }

      return [...compareData?.map((item) => ({ ...item, id: `${item.id}_compare` })), ...data];
    }

    return data;
  }, [cases, filteredCases, minDate, maxDate, minDateCompare, maxDateCompare, compareRange, preset, presetCompare, isChecked, proformaSentMinimum]);

  const presetCompareOptions = useMemo(() => {
    const options = [
      { label: 'Don\'t compare', value: '' }
    ];

    if ([presets.THIS_YEAR, presets.THIS_QUARTER, presets.THIS_MONTH].includes(preset)) {
      options?.push({ label: 'Previous period', value: presets.PREVIOUS_PERIOD });
    }

    if ([presets.THIS_QUARTER, presets.THIS_MONTH].includes(preset)) {
      options?.push({ label: 'Year ago', value: presets.YEAR_AGO });
    }

    return options;
  }, [preset]);


  const onRowClick = (caseId) => {
    history.push(`${routes.CASES}/${caseId}?view=USAGE`);
  };

  return (
    <div className='page-container reporting-page__container'>
      {loading && <LoadScreen />}
      <div className='page-title'>Billing Report</div>
      <div className='reporting-page__body'>
        <div className='billing-reporting-chart__container'>
          <div className='case-reporting-chart'>
            <LineChart
              data={casesChartData}
              colors={casesChartData.length > 1 ? ['var(--app-text-subtle)', tenantPrimaryColor] : tenantPrimaryColor}
              margin={{ top: 20, right: 15, bottom: 30, left: 40 }}
              entity='currency'
              yFormat={formatTooltip}
              yAxisFormat={formatAxis}
            />
          </div>
          <BillingReportingDateRange
            minDate={minDate}
            onMinDateChange={(date) => handleDateChange('minDate', date)}
            maxDate={maxDate}
            onMaxDateChange={(date) => handleDateChange('maxDate', date)}
            preset={preset}
            onPresetChange={handlePresetChange}
            presetCompare={presetCompare}
            onPresetChangeCompare={setPresetCompare}
            options={presetOptions}
            compareOptions={presetCompareOptions}
            isChecked={isChecked}
            onCheck={(e) => setChecked(e.target.checked)}
          />
        </div>
        <BillingReportingTable
          cases={filteredCases}
          surgeons={surgeons}
          hospitals={hospitals}
          procedures={procedures}
          onRowClick={(item) => onRowClick(item.caseRouteId || item.caseId || item.docId || item.id)}
          filter={filter}
          filterOptions={getFilterOptions()}
          onFilterChange={handleFilterChange}
          onRemoveFilter={handleRemoveFilterItem}
          users={users}
        />
      </div>
    </div>
  );
};

export default BillingReportingPage;
